import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { VehicleSimulationData, Workspace } from '../DataTypes';
import { AccountService } from './account.service';

// Data that gets sent from backend on app load
export type RuntimeConfig = {
  brouterEndpoint: string;
  buildVersion?: string;
  redirectToProd: boolean;
  chooseSimulationKernel: boolean;
};

// Get API base url based on current window location
export const getBaseUrl = (): string => {
  const { protocol, hostname, port, pathname } = window.location;

  // Construct the root URL (protocol + host)
  let baseUrl = `${protocol}//${hostname}`;
  if (port) {
    baseUrl += `:${port}`; // Include port if present (important for localhost)
  }

  // Keep everything in `pathname` (backend's serving path) before the Angular fragment
  return `${baseUrl}${pathname}`;
};

@Injectable({
  providedIn: 'root'
})
export class ShareDataService {
  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);

  private readonly vehicleSimulationData = signal<VehicleSimulationData | null>(null);
  setVehicleSimulationData(data: VehicleSimulationData) {
    this.vehicleSimulationData.update(() => data);
  }
  getVehicleSimulationData() {
    return this.vehicleSimulationData();
  }

  private readonly applicationTitle = 'Smart Quick Sim';
  getApplicationTitle() {
    return this.applicationTitle;
  }

  private readonly applicationClaim = 'A fast-track train simulation tool';
  getApplicationClaim() {
    return this.applicationClaim;
  }

  getFullApplicationName() {
    return `${this.applicationTitle} - ${this.applicationClaim}`;
  }

  private readonly applicationVersion = '1.0.0';
  getApplicationVersion() {
    return this.applicationVersion;
  }

  getBuildVersion(): string {
    return this.runtimeConfig()?.buildVersion ?? 'dev';
  }

  getBaseUrl(): string {
    return getBaseUrl();
  }

  readonly runtimeConfig = signal<RuntimeConfig>({
    // Default values, will be overridden by backend response
    brouterEndpoint: '',
    redirectToProd: false,
    chooseSimulationKernel: false
  });

  initializeRuntimeConfig(): Observable<RuntimeConfig> {
    return this.http.get<RuntimeConfig>(environment.apiURL + '/runtime-config').pipe(
      map((config: RuntimeConfig) => {
        config.brouterEndpoint = environment.apiURL + '/' + config.brouterEndpoint;
        console.log('Runtime config loaded:', config);
        let runtimeConfig = config;
        if (!environment.production) {
          runtimeConfig = {
            ...config,
            redirectToProd: false
          };
          console.log('Runtime config adjusted for development:', runtimeConfig);
        }
        this.runtimeConfig.set(runtimeConfig);
        return runtimeConfig;
      })
    );
  }

  private readonly _allAvailableWorkspaces = signal<Workspace[]>([]);
  private readonly _showOtherPrivateWorkspaces = signal(false);
  private readonly _displayedAvailableWorkspaces = computed(() => {
    const ownGid = this.accountService.accountSummary()?.gid;
    const workspaces = this._allAvailableWorkspaces();
    if (this._showOtherPrivateWorkspaces()) {
      return workspaces;
    }

    return workspaces.filter(workspace => {
      // Exclude private workspaces of other users
      return !(workspace.isPrivate && workspace.creatorGid !== ownGid);
    });
  });
  // Expose a read-only signal for available workspaces
  readonly availableWorkspaces = this._displayedAvailableWorkspaces;
  setAvailableWorkspaces(workspaces: Workspace[]): void {
    this._allAvailableWorkspaces.set(workspaces);
  }

  setShowOtherPrivateWorkspaces(show: boolean): void {
    this._showOtherPrivateWorkspaces.set(show);
  }
}
